<template>
  <div class="__date-picker" :class="{ 'ja-locale': this.$i18n.locale === 'ja' }">
    <VDatePicker
      :value="range"
      mode="range"
      is-inline
      :columns="$screens({ default: 2, lg: 2 })"
      :max-date="currentMaxDate"
      :min-date="currentMinDate"
      @input="handleChange"
      :locale="language"
      :masks="computedMasks"
      @dayclick="onDayClick"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapState } from 'vuex';
import moment from 'moment';

import VDatePicker from 'v-calendar/lib/components/date-picker.umd';
Vue.component('date-picker', VDatePicker);

export default {
  name: 'DatePicker',
  components: { VDatePicker },
  props: {
    range: {
      type: Object,
      default: () => {
        return { start: '', end: '' };
      },
    },
    rangeLimitStart: {
      type: Number,
    },
    rangeLimitEnd: {
      type: Number,
    },
    rangeLimitUnit: {
      type: String,
      default: 'day',
    },
    minDate: {
      type: Date,
    },
    maxDate: {
      type: Date,
    },
  },
  data() {
    return {
      isSelecting: false,
      baseDate: null,
    };
  },
  watch: {
    range(val) {
      this.$emit('change', val);
    },
  },
  computed: {
    ...mapState({
      language: state => state.language,
    }),

    computedMasks() {
      if (this.$i18n.locale === 'ja') {
        return {
          title: `YYYY M月`, // ja format: "2024 10月"
        };
      } else {
        return {
          title: 'MMMM YYYY', // en format: "October 2024"
        };
      }
    },

    currentRange: {
      get() {
        return this.range;
      },
      set(val) {
        this.$emit('change', val);
      },
    },

    currentMinDate() {
      if (this.rangeLimitStart) {
        if (this.isSelecting) {
          const limitStart = moment(this.baseDate).subtract(
            this.rangeLimitStart,
            this.rangeLimitUnit
          );

          if (limitStart.isAfter(this.minDate)) {
            return limitStart.toDate();
          } else {
            return this.minDate;
          }
        }
      }

      return this.minDate;
    },

    currentMaxDate() {
      if (this.rangeLimitEnd) {
        if (this.isSelecting) {
          const limitEnd = moment(this.baseDate).add(this.rangeLimitEnd, this.rangeLimitUnit);

          if (limitEnd.isBefore(this.maxDate)) {
            return limitEnd.toDate();
          } else {
            return this.maxDate;
          }
        }
      }

      return this.maxDate;
    },
  },
  methods: {
    handleChange(val) {
      this.isSelecting = false;
      this.$emit('change', val);
    },

    onDayClick(val) {
      if (val.isDisabled) return false;

      this.baseDate = val.id;
      this.isSelecting = true;
    },
  },
};
</script>
<style lang="scss" scoped>
.__date-picker ::v-deep {
  // v-calendar border
  .vc-container {
    border: none !important;
    box-shadow: none !important;
  }

  .vc-grid-cell {
    .on-left, .on-right {
      margin: 0;
    }
  }
  // weekday labels
  .vc-grid-cell[style*="grid-area: 1 /"] {
    .vc-weekday {
      position: relative;
    }
  }
}

.ja-locale ::v-deep {
  // First calendar (September)
  .vc-grid-cell-row-1.vc-grid-cell-col-1 {
    .vc-grid-container.vc-weeks {
      [style*="grid-area: 1 / 1"] .vc-weekday::before { content: '日' !important; }
      [style*="grid-area: 1 / 2"] .vc-weekday::before { content: '月' !important; }
      [style*="grid-area: 1 / 3"] .vc-weekday::before { content: '火' !important; }
      [style*="grid-area: 1 / 4"] .vc-weekday::before { content: '水' !important; }
      [style*="grid-area: 1 / 5"] .vc-weekday::before { content: '木' !important; }
      [style*="grid-area: 1 / 6"] .vc-weekday::before { content: '金' !important; }
      [style*="grid-area: 1 / 7"] .vc-weekday::before { content: '土' !important; }
    }
  }

  // Second calendar (October)
  .vc-grid-cell-row-1.vc-grid-cell-col-2 {
    .vc-grid-container.vc-weeks {
      [style*="grid-area: 1 / 1"] .vc-weekday::before { content: '日' !important; }
      [style*="grid-area: 1 / 2"] .vc-weekday::before { content: '月' !important; }
      [style*="grid-area: 1 / 3"] .vc-weekday::before { content: '火' !important; }
      [style*="grid-area: 1 / 4"] .vc-weekday::before { content: '水' !important; }
      [style*="grid-area: 1 / 5"] .vc-weekday::before { content: '木' !important; }
      [style*="grid-area: 1 / 6"] .vc-weekday::before { content: '金' !important; }
      [style*="grid-area: 1 / 7"] .vc-weekday::before { content: '土' !important; }
    }
  }

  // Common styles for both calendars
  .vc-grid-container.vc-weeks {
    .vc-weekday {
      color: transparent !important;

      &::before {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: white;
        color: #94A3B8;
      }
    }
  }
}
</style>